import React from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { Tag } from '../common/Tag';
import { fmtDate, fmtDateTime } from '../../utils/helpers';

function EngineResponse({ msg, onUseInReport }) {
  const { state } = useData();
  const toast = useToast();
  const { result } = msg;
  const { answer, sources, snippets, conflicts, entities } = result;

  const handleSavePack = () => {
    toast('Saved as evidence pack — available in Reports module', 'ok');
  };

  return (
    <div className="msg">
      <div className="msg-avatar engine">
        <i className="ti ti-message-chatbot" style={{ fontSize: 14 }} />
      </div>
      <div className="msg-body">
        <div className="msg-meta">
          Deterministic engine · {fmtDateTime(msg.at)}
          {answer.type === 'missing' ? (
            <Tag>No answer · evidence missing</Tag>
          ) : (
            <Tag variant="ok" dot>{sources.length} approved sources cited</Tag>
          )}
          {conflicts.length > 0 && (
            <Tag variant="warn" dot>{conflicts.length} conflict{conflicts.length > 1 ? 's' : ''} detected</Tag>
          )}
        </div>

        <div className="msg-content">
          {answer.type === 'missing' ? (
            <>
              <div className="medium mb-2">{answer.heading}</div>
              <div className="sm">{answer.body}</div>
              <div className="card-title mt-3" style={{ color: 'var(--warn)' }}>
                <i className="ti ti-alert-triangle" /> Missing inputs
              </div>
              <ul style={{ paddingLeft: 22, fontSize: 13 }}>
                {answer.missing.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
              <div className="xs muted mt-3" style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
                <i className="ti ti-info-circle" /> The engine will not fabricate an answer. Upload the relevant sources and re-query.
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 14, lineHeight: 1.65 }}>
                {answer.body}
                {answer.citations?.map((c, i) => (
                  <span key={i} className="cite-sup">{sources.indexOf(c) + 1}</span>
                ))}
              </div>

              {conflicts.length > 0 && (
                <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--warn-soft)', borderLeft: '3px solid var(--warn)', borderRadius: 2 }}>
                  <div className="card-title" style={{ color: 'var(--warn)', marginBottom: 8 }}>
                    <i className="ti ti-alert-triangle" /> Conflict in approved sources
                  </div>
                  {conflicts.map((c, i) => (
                    <div key={i} className="xs">
                      <div className="medium">Parameter: <span className="mono">{c.param}</span></div>
                      <table style={{ fontSize: 11, marginTop: 4, width: '100%' }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: 3 }}>Source</th>
                            <th style={{ textAlign: 'right', padding: 3 }}>Value</th>
                            <th style={{ textAlign: 'right', padding: 3 }}>Year</th>
                          </tr>
                        </thead>
                        <tbody>
                          {c.values.map((v, j) => (
                            <tr key={j}>
                              <td style={{ padding: 3 }}>{v.source.title.slice(0, 60)}</td>
                              <td style={{ textAlign: 'right', padding: 3 }} className="num">{v.value}</td>
                              <td style={{ textAlign: 'right', padding: 3 }} className="num">{v.source.year}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                  <div className="xs muted mt-2">Per URS-KNO-015, the engine flags the conflict rather than silently selecting a value. Human review required.</div>
                </div>
              )}
            </>
          )}

          <div className="card-title mt-4">Sources cited</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {snippets.map(({ index, item, snippet }) => (
              <div key={item.id} style={{ background: 'var(--surface-2)', padding: '10px 12px', borderRadius: 2, border: '1px solid var(--line)' }}>
                <div className="flex gap-2 center mb-1">
                  <Tag variant="solid">{index}</Tag>
                  <div className="medium sm">{item.title}</div>
                </div>
                <div className="xs muted mb-1">{item.source} · {item.year} · v{item.version || '1.0'} · approved {fmtDate(item.approvedAt)}</div>
                <div className="xs italic" style={{ color: 'var(--ink-2)' }}>"{snippet}"</div>
              </div>
            ))}
          </div>

          <div className="flex gap-2 mt-3" style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
            <button className="btn btn-sm" onClick={handleSavePack}>
              <i className="ti ti-bookmark" /> Save evidence pack
            </button>
            <button className="btn btn-sm" onClick={() => onUseInReport?.(msg)}>
              <i className="ti ti-file-export" /> Use in report
            </button>
            <button className="btn btn-sm" onClick={() => { navigator.clipboard?.writeText(answer.body); toast('Answer copied', 'ok'); }}>
              <i className="ti ti-copy" /> Copy
            </button>
            <button className="btn btn-sm btn-ghost"><i className="ti ti-thumb-up" /></button>
            <button className="btn btn-sm btn-ghost"><i className="ti ti-thumb-down" /></button>
          </div>

          <div className="xs muted mt-3" style={{ borderTop: '1px solid var(--line)', paddingTop: 8 }}>
            <i className="ti ti-info-circle" /> Generated {fmtDateTime(msg.at)} · Engine {msg.engineVersion} · Rule pack {msg.rulePack} · Template {msg.templateVersion} · Reproducible for same scope/version. Logged in audit trail.
          </div>
        </div>
      </div>
    </div>
  );
}

export default EngineResponse;